import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import {
  makeStyles,
  shorthands,
  tokens,
  TabList,
  Tab,
  Spinner,
  Text,
  Menu,
  MenuItem,
  MenuList,
  MenuPopover,
  MenuTrigger,
  Dialog,
  DialogTrigger,
  DialogSurface,
  DialogTitle,
  DialogBody,
  DialogActions,
  DialogContent,
  Button,
  Input,
  Textarea,
  Label,
  Field,
  Dropdown,
  Option,
  Toast,
  ToastTitle,
  ToastBody,
  Toaster,
  useToastController,
  useId,
} from '@fluentui/react-components';
import { MoreHorizontal20Regular, Edit20Regular, Delete20Regular, BarcodeScanner20Regular, Delete24Filled } from '@fluentui/react-icons';
import DataTable, { ColumnConfig } from '../components/DataTable';
import { apiFetch } from '../lib/apiFetch';
import { formatCalendarDate, formatBusinessDate } from '../lib/businessTime';

interface StockItem {
  id: string;
  identifier: string;
  variation: string;
  status: string;
  type: string;
  availableFrom: string;
  availableTill: string;
  createdAt: string;
}

interface ProductGroup {
  id: string;
  name: string;
  sku: string;
}

interface Variation {
  id: string;
  name: string;
  photoUrl?: string;
  price: number;
  size?: string;
}

interface HistoryAction {
  id: string;
  date: string;
  action: string;
  items: string;
  order: string;
  customer: string;
  employee: string;
}

const useStyles = makeStyles({
  container: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
  },
  content: {
    flex: 1,
    ...shorthands.padding('24px'),
    overflow: 'auto',
  },
  header: {
    ...shorthands.padding('24px', '24px', '16px', '24px'),
    ...shorthands.borderBottom('1px', 'solid', tokens.colorNeutralStroke2),
  },
  title: {
    fontSize: '24px',
    fontWeight: '600',
    marginBottom: '4px',
  },
  subtitle: {
    fontSize: '14px',
    color: tokens.colorNeutralForeground2,
  },
  tabList: {
    marginBottom: '24px',
  },
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    ...shorthands.padding('40px'),
  },
  statusContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  statusDot: {
    width: '8px',
    height: '8px',
    ...shorthands.borderRadius('50%'),
  },
  statusDotInStock: {
    backgroundColor: tokens.colorPaletteGreenBackground3,
  },
  statusDotUnavailable: {
    backgroundColor: tokens.colorPaletteRedBackground3,
  },
  statusDotOther: {
    backgroundColor: tokens.colorNeutralBackground6,
  },
  variationImage: {
    width: '40px',
    height: '40px',
    ...shorthands.borderRadius('4px'),
    objectFit: 'cover',
  },
  variationImagePlaceholder: {
    width: '40px',
    height: '40px',
    ...shorthands.borderRadius('4px'),
    backgroundColor: tokens.colorNeutralBackground3,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '12px',
    color: tokens.colorNeutralForeground3,
  },
  variationRowContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    width: '100%',
  },
  menuButton: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '28px',
    height: '28px',
    ...shorthands.borderRadius('4px'),
    cursor: 'pointer',
    backgroundColor: 'transparent',
    ...shorthands.border('none'),
    color: tokens.colorNeutralForeground2,
    '&:hover': {
      backgroundColor: tokens.colorNeutralBackground1Hover,
    },
  },
  menuButtonVisible: {
    opacity: 1,
  },
  menuButtonHidden: {
    opacity: 0,
  },
  settingsContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: '32px',
  },
  settingsSection: {
    backgroundColor: tokens.colorNeutralBackground1,
    ...shorthands.borderRadius('4px'),
    ...shorthands.padding('24px'),
    boxShadow: tokens.shadow8,
  },
  sectionTitle: {
    fontSize: '18px',
    fontWeight: '600',
    marginBottom: '16px',
  },
  formFields: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
  },
  bufferTimeRow: {
    display: 'flex',
    gap: '16px',
    alignItems: 'flex-end',
  },
  bufferTimeField: {
    flex: 1,
  },
  bufferControls: {
    display: 'flex',
    gap: '8px',
    alignItems: 'center',
  },
  bufferInput: {
    width: '100px',
  },
  settingsActions: {
    display: 'flex',
    justifyContent: 'space-between',
    marginTop: '32px',
  },
  imagesUploadContainer: {
    display: 'flex',
    gap: '16px',
    flexWrap: 'wrap',
  },
  uploadBox: {
    width: '120px',
    height: '120px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    ...shorthands.border('2px', 'dashed', tokens.colorNeutralStroke2),
    ...shorthands.borderRadius('4px'),
    cursor: 'pointer',
    gap: '4px',
    '&:hover': {
      ...shorthands.border('2px', 'dashed', tokens.colorBrandStroke1),
      backgroundColor: tokens.colorNeutralBackground1Hover,
    },
  },
  imageThumbnailContainer: {
    position: 'relative',
    width: '120px',
    height: '120px',
    '&:hover .delete-icon': {
      opacity: 1,
    },
  },
  imageThumbnail: {
    width: '120px',
    height: '120px',
    ...shorthands.borderRadius('4px'),
    objectFit: 'cover',
    ...shorthands.border('1px', 'solid', tokens.colorNeutralStroke2),
  },
  deleteIconButton: {
    position: 'absolute',
    top: '8px',
    right: '8px',
    width: '32px',
    height: '32px',
    backgroundColor: tokens.colorNeutralBackground1,
    ...shorthands.borderRadius('4px'),
    ...shorthands.border('1px', 'solid', tokens.colorNeutralStroke2),
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    opacity: 0,
    transition: 'opacity 0.2s',
    '&:hover': {
      backgroundColor: tokens.colorNeutralBackground1Hover,
    },
  },
  uploadBoxText: {
    fontSize: '14px',
    fontWeight: '600',
    color: tokens.colorBrandForeground1,
  },
  uploadBoxSubtext: {
    fontSize: '12px',
    color: tokens.colorNeutralForeground3,
  },
  imagesRecommendation: {
    fontSize: '14px',
    color: tokens.colorNeutralForeground2,
    marginBottom: '16px',
  },
  learnMoreLink: {
    color: tokens.colorBrandForeground1,
    textDecoration: 'none',
    '&:hover': {
      textDecoration: 'underline',
    },
  },
});

type TabValue = 'inventory' | 'variations' | 'settings' | 'online-store' | 'history' | 'downtime';

// Component for variation image cell with context menu
const VariationImageCell: React.FC<{
  variation: Variation;
  onEdit: (id: string) => void;
  onAddBarcode: (id: string) => void;
  onDelete: (id: string) => void;
}> = ({ variation, onEdit, onAddBarcode, onDelete }) => {
  const styles = useStyles();
  const [isRowHovered, setIsRowHovered] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const row = container.closest('tr');
    if (!row) return;

    const handleMouseEnter = () => setIsRowHovered(true);
    const handleMouseLeave = () => setIsRowHovered(false);

    row.addEventListener('mouseenter', handleMouseEnter);
    row.addEventListener('mouseleave', handleMouseLeave);

    return () => {
      row.removeEventListener('mouseenter', handleMouseEnter);
      row.removeEventListener('mouseleave', handleMouseLeave);
    };
  }, []);

  const handleClick = (e: React.MouseEvent, action: 'edit' | 'barcode' | 'delete') => {
    e.stopPropagation();
    switch (action) {
      case 'edit':
        onEdit(variation.id);
        break;
      case 'barcode':
        onAddBarcode(variation.id);
        break;
      case 'delete':
        onDelete(variation.id);
        break;
    }
  };

  return (
    <div ref={containerRef} className={styles.variationRowContainer}>
      {variation.photoUrl ? (
        <img
          src={variation.photoUrl}
          alt={variation.name}
          className={styles.variationImage}
        />
      ) : (
        <div className={styles.variationImagePlaceholder}>
          {variation.name.charAt(0)}
        </div>
      )}
      <Menu>
        <MenuTrigger disableButtonEnhancement>
          <div className={`${styles.menuButton} ${isRowHovered ? styles.menuButtonVisible : styles.menuButtonHidden}`}>
            <MoreHorizontal20Regular />
          </div>
        </MenuTrigger>
        <MenuPopover>
          <MenuList>
            <MenuItem icon={<Edit20Regular />} onClick={(e) => handleClick(e, 'edit')}>Edit</MenuItem>
            <MenuItem icon={<BarcodeScanner20Regular />} onClick={(e) => handleClick(e, 'barcode')}>Add barcode</MenuItem>
            <MenuItem icon={<Delete20Regular />} onClick={(e) => handleClick(e, 'delete')}>Delete</MenuItem>
          </MenuList>
        </MenuPopover>
      </Menu>
    </div>
  );
};

const ProductGroupDetails: React.FC = () => {
  const styles = useStyles();
  const { id } = useParams<{ id: string }>();
  const toasterId = useId('toaster');
  const { dispatchToast } = useToastController(toasterId);
  const [selectedTab, setSelectedTab] = useState<TabValue>('inventory');
  const [productGroup, setProductGroup] = useState<ProductGroup | null>(null);
  const [stockItems, setStockItems] = useState<StockItem[]>([]);
  const [variations, setVariations] = useState<Variation[]>([]);
  const [historyActions, setHistoryActions] = useState<HistoryAction[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [variationToDelete, setVariationToDelete] = useState<string | null>(null);

  // Edit variation dialog state
  const [editVariationDialogOpen, setEditVariationDialogOpen] = useState(false);
  const [editVariationId, setEditVariationId] = useState<string | null>(null);
  const [editVariationName, setEditVariationName] = useState('');

  // Barcode dialog state
  const [barcodeDialogOpen, setBarcodeDialogOpen] = useState(false);
  const [barcodeVariationId, setBarcodeVariationId] = useState<string | null>(null);
  const [barcodeValue, setBarcodeValue] = useState('');

  // Inline editing state
  const [editingPriceId, setEditingPriceId] = useState<string | null>(null);
  const [editingPriceValue, setEditingPriceValue] = useState<string>('');
  const [editingSizeId, setEditingSizeId] = useState<string | null>(null);
  const [editingSizeValue, setEditingSizeValue] = useState<string>('');

  // Settings form state
  const [settingsName, setSettingsName] = useState('');
  const [settingsSku, setSettingsSku] = useState('');
  const [settingsDescription, setSettingsDescription] = useState('');
  const [bufferBefore, setBufferBefore] = useState(0);
  const [bufferBeforeUnit, setBufferBeforeUnit] = useState('Hours');
  const [bufferAfter, setBufferAfter] = useState(0);
  const [bufferAfterUnit, setBufferAfterUnit] = useState('Hours');
  const [productImages, setProductImages] = useState<string[]>([]);

  // File upload ref
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (files && files.length > 0) {
      const file = files[0];
      const reader = new FileReader();

      reader.onload = async () => {
        const base64String = reader.result as string;

        dispatchToast(
          <Toast>
            <ToastTitle>Upload started</ToastTitle>
            <ToastBody>Uploading photo...</ToastBody>
          </Toast>,
          { intent: 'info' }
        );

        try {
          const requestBody = {
            method: 'PUT',
            path: `api/4/product_groups/${id}`,
            body: {
              data: {
                id: id,
                type: 'product_groups',
                attributes: {
                  photo_base64: base64String,
                }
              }
            }
          };

          const response = await apiFetch(`/booqable-proxy`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(requestBody),
          });

          if (!response.ok) {
            throw new Error('Failed to upload photo');
          }

          const updatedData = await response.json();
          const newPhotoUrl = updatedData.data?.attributes?.photo_url;

          if (newPhotoUrl) {
            setProductImages([newPhotoUrl]);
          }

          dispatchToast(
            <Toast>
              <ToastTitle>Upload complete</ToastTitle>
              <ToastBody>Photo has been uploaded successfully.</ToastBody>
            </Toast>,
            { intent: 'success' }
          );
        } catch (err) {
          console.error('Error uploading photo:', err);
          dispatchToast(
            <Toast>
              <ToastTitle>Upload failed</ToastTitle>
              <ToastBody>Failed to upload photo. Please try again.</ToastBody>
            </Toast>,
            { intent: 'error' }
          );
        }
      };

      reader.readAsDataURL(file);

      // Reset file input so the same file can be re-selected
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleDeleteImage = async (index: number) => {
    try {
      const requestBody = {
        method: 'PUT',
        path: `api/4/product_groups/${id}`,
        body: {
          data: {
            id: id,
            type: 'product_groups',
            attributes: {
              photo_base64: '',
            }
          }
        }
      };

      const response = await apiFetch(`/booqable-proxy`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(requestBody),
      });

      if (!response.ok) {
        throw new Error('Failed to remove photo');
      }

      // Remove from local state
      const updatedImages = productImages.filter((_, i) => i !== index);
      setProductImages(updatedImages);

      dispatchToast(
        <Toast>
          <ToastTitle>Image removed</ToastTitle>
          <ToastBody>The image has been removed from the product.</ToastBody>
        </Toast>,
        { intent: 'success' }
      );
    } catch (err) {
      console.error('Error deleting image:', err);
      dispatchToast(
        <Toast>
          <ToastTitle>Delete failed</ToastTitle>
          <ToastBody>Failed to remove image. Please try again.</ToastBody>
        </Toast>,
        { intent: 'error' }
      );
    }
  };

  // Just update the unit without converting the value
  const handleBufferBeforeUnitChange = (newUnit: string) => {
    setBufferBeforeUnit(newUnit);
  };

  const handleBufferAfterUnitChange = (newUnit: string) => {
    setBufferAfterUnit(newUnit);
  };

  // Fetch product group + variations + stock items + plannings — all in
  // one call via the consolidated /booqable/product-group/:id endpoint.
  // Previously this was an N+1 dance (1 group fetch + 1 items list + N stock
  // fetches + N planning fetches), which scaled badly with variation count.
  useEffect(() => {
    const fetchData = async () => {
      if (!id) return;

      try {
        setLoading(true);

        const res = await apiFetch(`/booqable/product-group/${id}`);
        if (!res.ok) throw new Error('Failed to fetch product group');
        const data = await res.json();

        const pg = data.product_group;
        const pgAttrs = pg?.attributes || {};

        setProductGroup({
          id: pg?.id ?? id,
          name: pgAttrs.name || 'Unknown Product',
          sku: pgAttrs.sku || 'N/A',
        });

        setSettingsName(pgAttrs.name || '');
        setSettingsSku(pgAttrs.sku || '');
        setSettingsDescription(pgAttrs.extra_information || pgAttrs.description || pgAttrs.excerpt || '');

        const images: string[] = [];
        if (pgAttrs.photo_url) images.push(pgAttrs.photo_url);
        setProductImages(images);

        if (pgAttrs.buffer_time_before !== undefined) {
          setBufferBefore(Math.floor(pgAttrs.buffer_time_before / 3600));
          setBufferBeforeUnit('Hours');
        }
        if (pgAttrs.buffer_time_after !== undefined) {
          setBufferAfter(Math.floor(pgAttrs.buffer_time_after / 3600));
          setBufferAfterUnit('Hours');
        }

        // Variations (Booqable: products inside the group).
        const variationItems = data.variations || [];
        const itemNameById: Record<string, string> = {};
        const allVariations: Variation[] = variationItems.map((item: any) => {
          const a = item.attributes || {};
          const name = a.variation_fields || a.name || '-';
          itemNameById[item.id] = a.name || a.variation_fields || '-';
          return {
            id: item.id,
            name,
            photoUrl: a.photo_url,
            price: a.base_price_in_cents ? a.base_price_in_cents / 100 : 0,
            size: a.variation_values || '-',
          };
        });
        setVariations(allVariations);

        // Stock items — already filtered to this product group by the
        // backend (uses filter[product_group_id], with a fallback to
        // per-variation filter[product_id] if Booqable returns nothing).
        const stockItemsData = data.stock_items || [];
        const variationNameByItemId: Record<string, string> = {};
        for (const v of allVariations) variationNameByItemId[v.id] = v.name;

        const mappedStockItems: StockItem[] = stockItemsData.map((s: any) => {
          const a = s.attributes || {};
          const productId = a.product_id
            ?? s.relationships?.product?.data?.id
            ?? '';
          return {
            id: s.id,
            identifier: a.identifier || s.id,
            variation: variationNameByItemId[productId] || '-',
            status: a.status || 'unknown',
            type: a.stock_item_type || 'rental',
            availableFrom: a.available_from ? formatCalendarDate(a.available_from) : '-',
            availableTill: a.available_till ? formatCalendarDate(a.available_till) : '-',
            createdAt: a.created_at ? formatBusinessDate(a.created_at) : '-',
          };
        });
        setStockItems(mappedStockItems);

        // Plannings — batched in the same response. The backend already
        // joined order + customer via Booqable's `include` mechanism.
        const plannings = data.plannings || [];
        const planningIncluded = data.plannings_included || [];

        const ordersMap: Record<string, any> = {};
        const customersMap: Record<string, any> = {};
        for (const inc of planningIncluded) {
          if (inc.type === 'orders') ordersMap[inc.id] = inc;
          if (inc.type === 'customers') customersMap[inc.id] = inc;
        }

        const allHistoryActions: HistoryAction[] = [];
        for (const planning of plannings) {
          const planAttrs = planning.attributes || {};
          const orderId = planning.relationships?.order?.data?.id;
          const orderData = ordersMap[orderId];
          const orderAttrs = orderData?.attributes || {};
          const custId = orderData?.relationships?.customer?.data?.id;
          const customerAttrs = customersMap[custId]?.attributes || {};

          // Skip cancelled orders so the history list stays signal.
          if (orderAttrs.status === 'canceled') continue;

          // Resolve variation name from the planning's item_id so each row
          // points to the right variation in mixed-variation product groups.
          const planItemId = planning.relationships?.item?.data?.id
            ?? planAttrs.item_id;
          const itemName = (planItemId && itemNameById[planItemId]) || '-';

          allHistoryActions.push({
            id: planning.id,
            date: planAttrs.starts_at ? formatCalendarDate(planAttrs.starts_at) : '-',
            action: planAttrs.status === 'started' ? 'Rented'
              : planAttrs.status === 'stopped' ? 'Returned'
                : 'Reserved',
            items: itemName,
            order: orderAttrs.number ? `#${orderAttrs.number}` : '-',
            customer: customerAttrs.name || '-',
            employee: '-',
          });
        }

        allHistoryActions.sort((a, b) => {
          const dateA = a.date !== '-' ? new Date(a.date).getTime() : 0;
          const dateB = b.date !== '-' ? new Date(b.date).getTime() : 0;
          return dateB - dateA;
        });
        setHistoryActions(allHistoryActions);

        setError(null);
      } catch (err) {
        setError('Failed to load product group details');
        console.error('Error fetching product group:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [id]);

  // Handler functions for variation menu actions
  const handleEditVariation = (variationId: string) => {
    const variation = variations.find(v => v.id === variationId);
    if (variation) {
      setEditVariationId(variationId);
      setEditVariationName(variation.name);
      setEditVariationDialogOpen(true);
    }
  };

  const handleAddBarcode = (variationId: string) => {
    setBarcodeVariationId(variationId);
    setBarcodeValue('');
    setBarcodeDialogOpen(true);
  };

  const handleDeleteVariation = (id: string) => {
    setVariationToDelete(id);
    setDeleteDialogOpen(true);
  };

  const confirmDeleteVariation = async () => {
    if (!variationToDelete) return;

    try {
      const requestBody = {
        method: 'DELETE',
        path: `api/4/products/${variationToDelete}`,
      };

      const response = await apiFetch(`/booqable-proxy`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(requestBody),
      });

      if (!response.ok) {
        throw new Error('Failed to delete variation');
      }

      // Remove from local state
      setVariations(variations.filter(v => v.id !== variationToDelete));

      dispatchToast(
        <Toast>
          <ToastTitle>Variation deleted</ToastTitle>
          <ToastBody>The variation has been deleted successfully.</ToastBody>
        </Toast>,
        { intent: 'success' }
      );

      // Close dialog and reset state
      setDeleteDialogOpen(false);
      setVariationToDelete(null);
    } catch (err) {
      console.error('Error deleting variation:', err);
      dispatchToast(
        <Toast>
          <ToastTitle>Delete failed</ToastTitle>
          <ToastBody>Failed to delete variation. Please try again.</ToastBody>
        </Toast>,
        { intent: 'error' }
      );
    }
  };

  const cancelDelete = () => {
    setDeleteDialogOpen(false);
    setVariationToDelete(null);
  };

  const confirmEditVariation = async () => {
    if (!editVariationId) return;

    try {
      const requestBody = {
        method: 'PUT',
        path: `api/4/products/${editVariationId}`,
        body: {
          data: {
            id: editVariationId,
            type: 'products',
            attributes: {
              name: editVariationName,
            }
          }
        }
      };

      const response = await apiFetch(`/booqable-proxy`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(requestBody),
      });

      if (!response.ok) {
        throw new Error('Failed to update variation');
      }

      // Update local state
      setVariations(variations.map(v =>
        v.id === editVariationId ? { ...v, name: editVariationName } : v
      ));

      dispatchToast(
        <Toast>
          <ToastTitle>Variation updated</ToastTitle>
          <ToastBody>The variation name has been updated.</ToastBody>
        </Toast>,
        { intent: 'success' }
      );

      setEditVariationDialogOpen(false);
      setEditVariationId(null);
    } catch (err) {
      console.error('Error updating variation:', err);
      dispatchToast(
        <Toast>
          <ToastTitle>Update failed</ToastTitle>
          <ToastBody>Failed to update variation. Please try again.</ToastBody>
        </Toast>,
        { intent: 'error' }
      );
    }
  };

  const confirmAddBarcode = async () => {
    if (!barcodeVariationId || !barcodeValue.trim()) return;

    try {
      const requestBody = {
        method: 'POST',
        path: 'api/4/barcodes',
        body: {
          data: {
            type: 'barcodes',
            attributes: {
              number: barcodeValue.trim(),
              barcode_type: 'code128',
            },
            relationships: {
              owner: {
                data: {
                  type: 'products',
                  id: barcodeVariationId,
                }
              }
            }
          }
        }
      };

      const response = await apiFetch(`/booqable-proxy`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(requestBody),
      });

      if (!response.ok) {
        throw new Error('Failed to add barcode');
      }

      dispatchToast(
        <Toast>
          <ToastTitle>Barcode added</ToastTitle>
          <ToastBody>Barcode has been added to the variation.</ToastBody>
        </Toast>,
        { intent: 'success' }
      );

      setBarcodeDialogOpen(false);
      setBarcodeVariationId(null);
      setBarcodeValue('');
    } catch (err) {
      console.error('Error adding barcode:', err);
      dispatchToast(
        <Toast>
          <ToastTitle>Add barcode failed</ToastTitle>
          <ToastBody>Failed to add barcode. Please try again.</ToastBody>
        </Toast>,
        { intent: 'error' }
      );
    }
  };

  // Inline editing handlers for Price
  const handlePriceClick = (variation: Variation) => {
    setEditingPriceId(variation.id);
    setEditingPriceValue(variation.price.toFixed(2));
  };

  const handlePriceSave = async (variationId: string) => {
    const newPrice = parseFloat(editingPriceValue);
    if (isNaN(newPrice)) {
      setEditingPriceId(null);
      return;
    }

    // Update local state immediately for snappy UI
    setVariations(variations.map(v =>
      v.id === variationId ? { ...v, price: newPrice } : v
    ));

    // Exit edit mode immediately
    setEditingPriceId(null);

    // Make API call in the background
    try {
      const bodyData = {
        data: {
          id: variationId,
          type: 'products',
          attributes: {
            base_price_in_cents: Math.round(newPrice * 100), // Convert to cents
          }
        }
      };

      const requestBody = {
        method: 'PUT',
        path: `api/4/products/${variationId}`,
        body: bodyData
      };

      const response = await apiFetch(`/booqable-proxy`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(requestBody),
      });

      if (!response.ok) {
        throw new Error('Failed to update price');
      }

      // Show success toast
      dispatchToast(
        <Toast>
          <ToastTitle>Price updated</ToastTitle>
          <ToastBody>Price has been updated to ${newPrice.toFixed(2)}</ToastBody>
        </Toast>,
        { intent: 'success' }
      );
    } catch (err) {
      console.error('Error updating price:', err);

      // Revert the optimistic update on error
      // TODO: Could fetch fresh data here to ensure consistency

      // Show error toast
      dispatchToast(
        <Toast>
          <ToastTitle>Update failed</ToastTitle>
          <ToastBody>Failed to update price. Please try again.</ToastBody>
        </Toast>,
        { intent: 'error' }
      );
    }
  };

  const handlePriceCancel = () => {
    setEditingPriceId(null);
    setEditingPriceValue('');
  };

  // Inline editing handlers for Size
  const handleSizeClick = (variation: Variation) => {
    setEditingSizeId(variation.id);
    setEditingSizeValue(variation.size || '');
  };

  const handleSizeSave = async (variationId: string) => {
    // Update local state immediately for snappy UI
    setVariations(variations.map(v =>
      v.id === variationId ? { ...v, size: editingSizeValue } : v
    ));

    // Exit edit mode immediately
    setEditingSizeId(null);

    // Make API call in the background
    try {
      const bodyData = {
        data: {
          id: variationId,
          type: 'products',
          attributes: {
            variation_values: [editingSizeValue], // Must be an array
          }
        }
      };

      const requestBody = {
        method: 'PUT',
        path: `api/4/products/${variationId}`,
        body: bodyData
      };

      const response = await apiFetch(`/booqable-proxy`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(requestBody),
      });

      if (!response.ok) {
        throw new Error('Failed to update size');
      }

      // Show success toast
      dispatchToast(
        <Toast>
          <ToastTitle>Size updated</ToastTitle>
          <ToastBody>Size has been updated to {editingSizeValue}</ToastBody>
        </Toast>,
        { intent: 'success' }
      );
    } catch (err) {
      console.error('Error updating size:', err);

      // Revert the optimistic update on error
      // TODO: Could fetch fresh data here to ensure consistency

      // Show error toast
      dispatchToast(
        <Toast>
          <ToastTitle>Update failed</ToastTitle>
          <ToastBody>Failed to update size. Please try again.</ToastBody>
        </Toast>,
        { intent: 'error' }
      );
    }
  };

  const handleSizeCancel = () => {
    setEditingSizeId(null);
    setEditingSizeValue('');
  };

  // Convert buffer times from the selected unit to seconds
  const convertToSeconds = (value: number, unit: string): number => {
    switch (unit) {
      case 'Minutes':
        return value * 60;
      case 'Hours':
        return value * 3600;
      case 'Days':
        return value * 86400;
      default:
        return value * 3600; // Default to hours
    }
  };

  const handleSaveSettings = async () => {
    try {
      const bodyData = {
        data: {
          id: id, // Product group ID
          type: 'product_groups',
          attributes: {
            name: settingsName,
            sku: settingsSku,
            extra_information: settingsDescription,
            buffer_time_before: convertToSeconds(bufferBefore, bufferBeforeUnit),
            buffer_time_after: convertToSeconds(bufferAfter, bufferAfterUnit),
          }
        }
      };

      // The backend expects: { method: 'PUT', path: '...', body: {...} }
      const requestBody = {
        method: 'PUT',
        path: `api/4/product_groups/${id}`,
        body: bodyData
      };

      console.log('Saving settings with body:', requestBody);

      const response = await apiFetch(
        `/booqable-proxy`,
        {
          method: 'POST', // Always POST to the proxy
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(requestBody),
        }
      );

      if (!response.ok) {
        const errorText = await response.text();
        console.error('Response error:', errorText);
        throw new Error(`Failed to update product group: ${response.statusText} - ${errorText}`);
      }

      const updatedData = await response.json();
      console.log('Settings saved successfully:', updatedData);

      // Update local state with saved data
      const attrs = updatedData.data.attributes;
      setProductGroup({
        id: updatedData.data.id,
        name: attrs.name || 'Unknown Product',
        sku: attrs.sku || 'N/A',
      });

      // Show success notification
      dispatchToast(
        <Toast>
          <ToastTitle>Settings saved</ToastTitle>
          <ToastBody>Product group settings have been updated successfully.</ToastBody>
        </Toast>,
        { intent: 'success' }
      );
    } catch (err) {
      console.error('Error saving settings:', err);
      // Show error notification
      dispatchToast(
        <Toast>
          <ToastTitle>Save failed</ToastTitle>
          <ToastBody>Failed to save settings. Please try again.</ToastBody>
        </Toast>,
        { intent: 'error' }
      );
    }
  };

  const handleCancelSettings = () => {
    // Reset form to original values
    if (productGroup) {
      setSettingsName(productGroup.name);
      setSettingsSku(productGroup.sku);
    }
    // TODO: Reload from API to get fresh data
  };

  // Define columns for variations DataTable
  const variationColumns: ColumnConfig<Variation>[] = [
    {
      columnKey: 'photoUrl',
      label: 'Image',
      width: '180px',
      sortable: false,
      groupable: false,
      render: (variation) => (
        <VariationImageCell
          variation={variation}
          onEdit={handleEditVariation}
          onAddBarcode={handleAddBarcode}
          onDelete={handleDeleteVariation}
        />
      ),
    },
    {
      columnKey: 'price',
      label: 'Price',
      sortable: true,
      groupable: false,
      render: (variation) => {
        const isEditing = editingPriceId === variation.id;

        if (isEditing) {
          return (
            <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
              <Input
                value={editingPriceValue}
                onChange={(e, data) => setEditingPriceValue(data.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    handlePriceSave(variation.id);
                  } else if (e.key === 'Escape') {
                    handlePriceCancel();
                  }
                }}
                onBlur={() => handlePriceSave(variation.id)}
                onFocus={(e) => e.target.select()}
                autoFocus
                style={{ width: '100px' }}
              />
            </div>
          );
        }

        return (
          <span
            onClick={() => handlePriceClick(variation)}
            style={{
              cursor: 'pointer',
              padding: '4px 8px',
              borderRadius: '4px',
              display: 'inline-block',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = tokens.colorNeutralBackground1Hover;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'transparent';
            }}
          >
            ${variation.price.toFixed(2)}
          </span>
        );
      },
    },
    {
      columnKey: 'size',
      label: 'Size',
      sortable: true,
      groupable: true,
      render: (variation) => {
        const isEditing = editingSizeId === variation.id;

        if (isEditing) {
          return (
            <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
              <Input
                value={editingSizeValue}
                onChange={(e, data) => setEditingSizeValue(data.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    handleSizeSave(variation.id);
                  } else if (e.key === 'Escape') {
                    handleSizeCancel();
                  }
                }}
                onBlur={() => handleSizeSave(variation.id)}
                onFocus={(e) => e.target.select()}
                autoFocus
                style={{ width: '150px' }}
              />
            </div>
          );
        }

        return (
          <span
            onClick={() => handleSizeClick(variation)}
            style={{
              cursor: 'pointer',
              padding: '4px 8px',
              borderRadius: '4px',
              display: 'inline-block',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = tokens.colorNeutralBackground1Hover;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'transparent';
            }}
          >
            {variation.size || '-'}
          </span>
        );
      },
    },
  ];

  // Define columns for history DataTable
  const historyColumns: ColumnConfig<HistoryAction>[] = [
    {
      columnKey: 'date',
      label: 'Date',
      sortable: true,
      groupable: true,
    },
    {
      columnKey: 'action',
      label: 'Action',
      sortable: true,
      groupable: true,
    },
    {
      columnKey: 'items',
      label: 'Items',
      sortable: false,
      groupable: false,
    },
    {
      columnKey: 'order',
      label: 'Order',
      sortable: true,
      groupable: false,
    },
    {
      columnKey: 'customer',
      label: 'Customer',
      sortable: true,
      groupable: true,
    },
    {
      columnKey: 'employee',
      label: 'Employee',
      sortable: true,
      groupable: true,
    },
  ];

  // Define columns for stock items DataTable
  const columns: ColumnConfig<StockItem>[] = [
    {
      columnKey: 'identifier',
      label: 'Identifier',
      sortable: true,
      groupable: false,
    },
    {
      columnKey: 'variation',
      label: 'Variation',
      sortable: true,
      groupable: true,
    },
    {
      columnKey: 'status',
      label: 'Status',
      sortable: true,
      groupable: true,
      render: (item) => {
        const statusLabel = item.status === 'in_stock' ? 'In stock' :
                           item.status === 'unavailable' ? 'Unavailable' :
                           item.status;

        const dotClass = item.status === 'in_stock' ? styles.statusDotInStock :
                        item.status === 'unavailable' ? styles.statusDotUnavailable :
                        styles.statusDotOther;

        return (
          <div className={styles.statusContainer}>
            <div className={`${styles.statusDot} ${dotClass}`} />
            <span>{statusLabel}</span>
          </div>
        );
      },
    },
    {
      columnKey: 'type',
      label: 'Type',
      sortable: true,
      groupable: true,
      render: (item) => {
        // Capitalize the first letter of each word
        const capitalizedType = item.type
          .split('_')
          .map(word => word.charAt(0).toUpperCase() + word.slice(1))
          .join(' ');
        return <span>{capitalizedType}</span>;
      },
    },
    {
      columnKey: 'availableFrom',
      label: 'Available from',
      sortable: true,
      groupable: false,
    },
    {
      columnKey: 'availableTill',
      label: 'Available till',
      sortable: true,
      groupable: false,
    },
    {
      columnKey: 'createdAt',
      label: 'Created at',
      sortable: true,
      groupable: false,
    },
  ];

  if (loading) {
    return (
      <>
        <Toaster toasterId={toasterId} />
        <div className={styles.loadingContainer}>
          <Spinner label="Loading product group details..." />
        </div>
      </>
    );
  }

  if (error || !productGroup) {
    return (
      <>
        <Toaster toasterId={toasterId} />
        <div className={styles.loadingContainer}>
          <span>{error || 'Product group not found'}</span>
        </div>
      </>
    );
  }

  return (
    <>
      <Toaster toasterId={toasterId} />
      <div className={styles.container}>
      <div className={styles.header}>
        <Text className={styles.title}>{productGroup.name}</Text>
      </div>
      <div className={styles.content}>
        <TabList
          className={styles.tabList}
          selectedValue={selectedTab}
          onTabSelect={(_, data) => setSelectedTab(data.value as TabValue)}
        >
          <Tab value="inventory">Inventory</Tab>
          <Tab value="variations">Variations</Tab>
          <Tab value="settings">Settings</Tab>
          <Tab value="online-store">Online Store</Tab>
          <Tab value="history">History</Tab>
          <Tab value="downtime">Downtime</Tab>
        </TabList>

        {selectedTab === 'inventory' && (
          <DataTable
            data={stockItems}
            columns={columns}
            keyField="id"
            hideableColumns
            columnVisibilityStorageKey="product_group_inventory"
            itemsPerPage={25}
            showCheckboxes={false}
          />
        )}

        {selectedTab === 'variations' && (
          <DataTable
            data={variations}
            columns={variationColumns}
            keyField="id"
            hideableColumns
            columnVisibilityStorageKey="product_group_variations"
            itemsPerPage={25}
            showCheckboxes={false}
          />
        )}

        {selectedTab === 'settings' && (
          <div className={styles.settingsContainer}>
            {/* General Information Section */}
            <div className={styles.settingsSection}>
              <Text className={styles.sectionTitle}>General information</Text>
              <div className={styles.formFields}>
                <Field label="Name">
                  <Input
                    value={settingsName}
                    onChange={(e, data) => setSettingsName(data.value)}
                  />
                </Field>
                <Field label="Stock Keeping Unit (SKU)">
                  <Input
                    value={settingsSku}
                    onChange={(e, data) => setSettingsSku(data.value)}
                  />
                </Field>
                <Field label="Description on documents">
                  <Textarea
                    value={settingsDescription}
                    onChange={(e, data) => setSettingsDescription(data.value)}
                    resize="vertical"
                  />
                </Field>
              </div>
            </div>

            {/* Images Section */}
            <div className={styles.settingsSection}>
              <Text className={styles.sectionTitle}>Images</Text>
              <div className={styles.imagesRecommendation}>
                Recommended: JPEG images, 1200x1200 pixels or larger.{' '}
                <a href="#" className={styles.learnMoreLink}>Learn more ↗</a>
              </div>
              <div className={styles.imagesUploadContainer}>
                {/* Display existing images */}
                {productImages.map((imageUrl, index) => (
                  <div key={index} className={styles.imageThumbnailContainer}>
                    <img
                      src={imageUrl}
                      alt={`Product image ${index + 1}`}
                      className={styles.imageThumbnail}
                    />
                    <div
                      className={`${styles.deleteIconButton} delete-icon`}
                      onClick={() => handleDeleteImage(index)}
                    >
                      <Delete24Filled />
                    </div>
                  </div>
                ))}

                {/* Upload box */}
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={handleFileChange}
                  style={{ display: 'none' }}
                />
                <div className={styles.uploadBox} onClick={handleUploadClick}>
                  <Text className={styles.uploadBoxText}>Upload</Text>
                  <Text className={styles.uploadBoxSubtext}>or drop files</Text>
                </div>
              </div>
            </div>

            {/* Buffer Time Section — UI removed, see GH issue. Wiring (state, handlers,
                save payload at handleSaveSettings) intentionally retained for later restore. */}

            {/* Actions */}
            <div className={styles.settingsActions}>
              <Button appearance="primary" style={{ backgroundColor: '#d13438', borderColor: '#d13438' }}>
                Delete product
              </Button>
              <div style={{ display: 'flex', gap: '8px' }}>
                <Button appearance="secondary" onClick={handleCancelSettings}>Cancel</Button>
                <Button appearance="primary" onClick={handleSaveSettings}>Save</Button>
              </div>
            </div>
          </div>
        )}

        {selectedTab === 'online-store' && (
          <div>Online Store content coming soon...</div>
        )}

        {selectedTab === 'history' && (
          <DataTable
            data={historyActions}
            columns={historyColumns}
            keyField="id"
            hideableColumns
            columnVisibilityStorageKey="product_group_history"
            itemsPerPage={25}
            showCheckboxes={false}
          />
        )}

        {selectedTab === 'downtime' && (
          <div>Downtime content coming soon...</div>
        )}
      </div>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={(_, data) => setDeleteDialogOpen(data.open)}>
        <DialogSurface>
          <DialogBody>
            <DialogTitle>Delete variation</DialogTitle>
            <DialogContent>
              Are you sure you want to delete this product variation? This cannot be undone.
            </DialogContent>
            <DialogActions>
              <Button appearance="secondary" onClick={cancelDelete}>
                Cancel
              </Button>
              <Button appearance="primary" onClick={confirmDeleteVariation}>
                Delete variation
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>

      {/* Edit Variation Dialog */}
      <Dialog open={editVariationDialogOpen} onOpenChange={(_, data) => setEditVariationDialogOpen(data.open)}>
        <DialogSurface>
          <DialogBody>
            <DialogTitle>Edit variation</DialogTitle>
            <DialogContent>
              <Field label="Variation name">
                <Input
                  value={editVariationName}
                  onChange={(e, data) => setEditVariationName(data.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') confirmEditVariation();
                  }}
                  autoFocus
                />
              </Field>
            </DialogContent>
            <DialogActions>
              <Button appearance="secondary" onClick={() => setEditVariationDialogOpen(false)}>
                Cancel
              </Button>
              <Button appearance="primary" onClick={confirmEditVariation}>
                Save
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>

      {/* Add Barcode Dialog */}
      <Dialog open={barcodeDialogOpen} onOpenChange={(_, data) => setBarcodeDialogOpen(data.open)}>
        <DialogSurface>
          <DialogBody>
            <DialogTitle>Add barcode</DialogTitle>
            <DialogContent>
              <Field label="Barcode number">
                <Input
                  value={barcodeValue}
                  onChange={(e, data) => setBarcodeValue(data.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') confirmAddBarcode();
                  }}
                  placeholder="Enter barcode number"
                  autoFocus
                />
              </Field>
            </DialogContent>
            <DialogActions>
              <Button appearance="secondary" onClick={() => setBarcodeDialogOpen(false)}>
                Cancel
              </Button>
              <Button appearance="primary" onClick={confirmAddBarcode} disabled={!barcodeValue.trim()}>
                Add barcode
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>
    </div>
    </>
  );
};

export default ProductGroupDetails;
